import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  SupportedCurrency,
  TokenPaymentMethod,
} from '@sorare/core/src/__generated__/globalTypes';
import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MY_CLUB_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMangopayCreditCardsEnabled } from '@sorare/core/src/hooks/useMangopayCreditCardsEnabled';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary, payment } from '@sorare/core/src/lib/glossary';
import { formatGqlErrors } from '@sorare/core/src/lib/gql';
import type { MonetaryAmountParams } from '@sorare/core/src/lib/monetaryAmount';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import { LazyPaymentProvider } from 'components/buyActions/LazyPaymentProvider';
import type { PrimaryBuyConfirmationOptions } from 'contexts/buyingConfirmation';
import { useBuyConfirmationContext } from 'contexts/buyingConfirmation';
import { useAcceptOffer } from 'hooks/offers/useAcceptOffer';

import type { CartPayment_cart } from './__generated__/index.graphql';
import { usePollCartBought } from './usePollCartBought';
import { useRefreshCartPriceAndSignedAmount } from './useRefreshCartPriceAndSignedAmount';

type Props = {
  myCart: CartPayment_cart;
  onSuccess: (anyCardIds: string[]) => void;
  beforeSubmitCallback?: () => Promise<void>;
  primaryBuyConfirmationOptions?: Partial<PrimaryBuyConfirmationOptions>;
};
export const CartPayment = ({
  myCart,
  onSuccess,
  beforeSubmitCallback,
  primaryBuyConfirmationOptions,
}: Props) => {
  const useMangopayCreditCards = useMangopayCreditCardsEnabled();
  const { showNotification } = useSnackNotificationContext();

  const { setShowBuyingConfirmation, setBuyConfirmationProps } =
    useBuyConfirmationContext();
  const { formatMessage } = useIntlContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { refresh } = useRefreshCartPriceAndSignedAmount(myCart.sport);

  const { generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();

  const { price: cartPrice, signedAmount, mainCartItems } = myCart;
  const price = toMonetaryAmount(cartPrice || MonetaryAmount.zero);

  const receiveCards = mainCartItems.map(item => item.card);

  const [priceInfos, setPriceInfos] = useState<{
    price: MonetaryAmountParams;
    signedAmount: string;
  } | null>(null);

  const [polling, setPolling] = useState(false);
  const [timeoutPolling, setTimeoutPolling] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const acceptOffer = useAcceptOffer();

  const afterPrepareAcceptOffer = (offerId: string) => {
    setBuyConfirmationProps({
      primaryBuyId: offerId,
      primaryBuyConfirmationOptions: {
        actions: {
          primary: {
            label: <FormattedMessage {...glossary.gotIt} />,
          },
          secondary: {
            label: <FormattedMessage {...glossary.viewGallery} />,
            to: generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS, {
              params: {
                slug: currentUser?.slug || '',
              },
            }),
          },
        },
        ...primaryBuyConfirmationOptions,
      },
    });
  };

  const onPaymentSuccess = (cardIds?: string[]) => {
    setShowBuyingConfirmation(true);
    onSuccess?.(cardIds || []);
  };

  const onPollingEnd = (offerId: string | false, cardIds?: string[]) => {
    setPolling(false);
    if (timeoutPolling) {
      clearTimeout(timeoutPolling);
    }
    if (offerId) {
      afterPrepareAcceptOffer(offerId);
      onPaymentSuccess(cardIds);
      return;
    }
    showNotification('paymentFailed');
  };

  usePollCartBought(
    polling,
    myCart.sport,
    myCart.currentPrimaryOffer?.id,
    onPollingEnd
  );

  const refreshCartAndSignedAmountBeforeBuy = async () => {
    const refreshResult = await refresh();
    if (refreshResult?.error)
      return { err: formatGqlErrors([refreshResult.error]) };
    const { price: refreshPrice, signedAmount: refreshSignedAmount } =
      refreshResult?.data?.currentUser?.myCart || {};
    const refreshPriceMonetaryAmount = toMonetaryAmount(
      refreshPrice || MonetaryAmount.zero
    );
    if (
      price &&
      refreshSignedAmount &&
      refreshPrice &&
      refreshSignedAmount !== priceInfos?.signedAmount
    ) {
      setPriceInfos({
        price: refreshPrice,
        signedAmount: refreshSignedAmount,
      });
      if (refreshPriceMonetaryAmount.referenceValue !== price.referenceValue) {
        // if price has changed, we need to update the payment box
        return {
          warnings: [
            formatMessage({
              id: 'primaryBuyField.Error.paymentBox.priceChangedV2',
              defaultMessage:
                'The price has changed, please review the offer and try again.',
            }),
          ],
        };
      }
    }

    return { signedAmount: refreshSignedAmount };
  };

  const buyWithWallet = async ({
    conversionCreditIds,
    supportedCurrency,
    tokenPaymentMethod,
    applePayPaymentToken,
  }: {
    monetaryAmount: MonetaryAmount;
    conversionCreditIds: string[];
    supportedCurrency: SupportedCurrency;
    tokenPaymentMethod?: TokenPaymentMethod;
    applePayPaymentToken?: ApplePayJS.ApplePayPaymentToken;
  }) => {
    const refreshResult = await refreshCartAndSignedAmountBeforeBuy();
    if (refreshResult?.err?.length) return refreshResult;
    if (!refreshResult.signedAmount || !signedAmount)
      return {
        err: [
          formatMessage({
            id: 'primaryBuyField.Error.paymentBox.priceUnvailable',
            defaultMessage: 'The card is unavailable, please try again later.',
          }),
        ],
      };
    const { errors, deal, cardIds } = await acceptOffer({
      cartId: myCart.id,
      conversionCreditIds,
      receiveCards,
      supportedCurrency,
      attemptReference: null,
      tokenPaymentMethod,
      signedAmount: refreshResult.signedAmount || signedAmount || undefined,
      applePayPaymentToken,
      afterPrepareAcceptOffer,
    });
    if (deal) {
      return onPaymentSuccess(cardIds);
    }
    return { err: errors };
  };
  // For mangopay, we do not know if the 3DS has failed or not. We need to fetch the primary offer and check buyer
  const onPaymentSuccessWrapper = () => {
    if (useMangopayCreditCards) {
      setPolling(true);
      setTimeoutPolling(
        setTimeout(() => {
          onPollingEnd(false);
        }, 20000)
      );
    } else {
      onPaymentSuccess();
    }
  };

  return (
    <Vertical>
      <LazyPaymentProvider
        paymentProps={{
          canChangeRefCurrency: true,
          objectId: myCart.id,
          objectType: 'cart',
          onSuccess: onPaymentSuccessWrapper,
          onBeforeBuyWithCreditCard: refreshCartAndSignedAmountBeforeBuy,
          signedAmount: signedAmount || undefined,
          onSubmit: buyWithWallet,
          price,
          cta: payment.confirmAndPay,
          canUseConversionCredit: true,
          currencies: [Currency.FIAT, Currency.ETH],
          sport: myCart.sport,
          onlyCreditCards: false,
          useKlarna: true,
        }}
        paymentBoxProps={{
          loadingPolling: polling,
          beforeSubmitCallback,
        }}
      />
    </Vertical>
  );
};

CartPayment.fragments = {
  cart: gql`
    fragment CartPayment_cart on Cart {
      id
      sport
      signedAmount
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      mainCartItems {
        id
        card {
          slug
          ...useAcceptOffer_anyCard
        }
      }
      currentPrimaryOffer {
        id
      }
    }
    ${useAcceptOffer.fragments.anyCard}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<CartPayment_cart>,
};
